import * as CL from '@design-system/component-library';
import { useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { JsonLd } from 'react-schemaorg';
import { pushToDataLayer } from '../../common/analytics';
import { paths } from '../../common/constants/pathVariables';
import { downloadSpeed, kBitsPerSecondMsg, mBpsMsg, t, uploadSpeedMsg } from '../../common/i18n';
import { deepEqual } from '../../common/utils/objectUtils';
import { SiteContext } from '../../public/site/SiteContext';
import { Grid } from '../Grid/Grid';
import { getViewItemListEventForPlaceOrder } from '../PlaceOrder/utils/placeOrderAnalyticsUtils';
import { handleChangeOffer } from './OrderUtils';
import { SubscriptionCard } from './SubscriptionCard';
import { MobileCarouselWrapper, addOfferToBasket, getItemList } from './SubscriptionUtils';
import type { SubCardWithAnalyticsV4Props } from './subscriptionCardsAnalyticsUtils';
import type { OrderProps, SubscriptionCardWithModel } from './types';
import type { OrderM2MLoaderData, SubscriptionUpdateLoaderData } from '../../common/loaders';
import type { State } from '../../selfservice/common/store';
import type { OfferProductMapType } from '../OrderSubscriptionSelection/OrderSubscriptionSelectionUtils';
import type { ItemList } from 'schema-dts';

export interface HorizontalSubscriptionsProps {
  subscriptionCards: SubscriptionCardWithModel[];
  wide?: boolean;
  compact?: boolean;
  analyticsListName: string;
  isChangeOffer: boolean;
  offerAndFeeMap?: OfferProductMapType[];
  hideGroupHeaders?: boolean;
}

export const HorizontalM2MSubscriptions = ({
  subscriptionCards,
  wide = false,
  compact = false,
  analyticsListName,
  isChangeOffer = false,
  offerAndFeeMap = [],
  hideGroupHeaders = false,
}: HorizontalSubscriptionsProps) => {
  const { search, pathname } = useLocation();
  const { siteBaseUrl } = useContext(SiteContext);
  const navigate = useNavigate();
  const noeLoaderData = useLoaderData() as OrderM2MLoaderData;
  const subscriptionLoaderData = useLoaderData() as SubscriptionUpdateLoaderData;

  const buttonText = t.B5J1('Add to basket');

  const hasRunOnceRef = useRef(false);
  if (!hasRunOnceRef.current && subscriptionCards.every(subCard => subCard.onlineModel?.onlineModelName)) {
    const analyticsEvent = getViewItemListEventForPlaceOrder(
      subscriptionCards as SubCardWithAnalyticsV4Props[],
      analyticsListName
    );
    pushToDataLayer(analyticsEvent);
    hasRunOnceRef.current = true;
  }

  let analyticsIndexLaitenetti4G = 0;
  let analyticsIndexLaitenetti5G = 0;

  const { campaignsFromVoucher = [], campaignContextsFromVoucher = [] } = useSelector((reduxState: State) => {
    const campaigns = reduxState.campaigns;
    return {
      campaignsFromVoucher: campaigns?.campaigns,
      campaignContextsFromVoucher: campaigns?.campaignContexts,
      voucher: campaigns?.voucher,
    };
  }, deepEqual);

  const getSubscription = (cardName: string): SubscriptionCardWithModel | undefined =>
    (subscriptionCards as SubscriptionCardWithModel[])?.find(card => card.cardName === cardName);

  const onChangeOffer = ({ onlineModel, laitenettiOfferName, quantity, voucherCode, services }: OrderProps) => {
    const additionalOffers = [...(noeLoaderData.m2mModel?.offers?.length ? noeLoaderData.m2mModel.offers : [])];

    handleChangeOffer({
      onlineModel,
      navigate,
      search,
      quantity,
      voucherCode,
      services,
      offerAndFeeMap,
      campaignsFromVoucher,
      campaignContextsFromVoucher,
      laitenettiOfferName,
      additionalOffers,
      addOnRules: noeLoaderData.addOnRules,
      subscription: subscriptionLoaderData.subscription,
      confirmationPath: paths.PS_MOBILE_SUBSCRIPTION_UPDATE_CONFIRMATION,
    });
  };

  const sharedProps = {
    isChangeOffer,
    analyticsListName,
    buttonMakesOrder: true,
    onOrder: isChangeOffer ? onChangeOffer : addOfferToBasket,
    hasQuantity: !isChangeOffer,
    showFixedTermInfo: true,
    compact,
  };

  const LaitenettiS = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription('S')}
      {...sharedProps}
      analyticsIndex={analyticsIndexLaitenetti4G++}
      badges={[]}
      subHeader="Yritysliittymä"
      header="Laitenetti S"
      description={t.A7CC('Sending photos, SMS/voice-controlled devices, property alarms, air source heat pumps')}
      infoItems={[
        {
          icon: 'download',
          text: `${t.R4O4(downloadSpeed)} ${t.B69Q(kBitsPerSecondMsg, '256')}`,
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.B69Q(kBitsPerSecondMsg, '256')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '5,5'),
        },
      ]}
      buttonText={buttonText}
    />
  );

  const LaitenettiM = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription('M')}
      {...sharedProps}
      analyticsIndex={analyticsIndexLaitenetti4G++}
      badges={[<CL.Badge key="suositus" color="blue-subtle" text={t.TJPP('Recommended')} />]}
      subHeader="Yritysliittymä"
      header="Laitenetti M"
      description={t.A7CD(
        'For surveillance and trail cameras (photos and videos), for alarm devices, for GPS trackers, for condition monitoring sensors'
      )}
      infoItems={[
        {
          icon: 'download',
          text: `${t.R4O4(downloadSpeed)} ${t.B69Q(kBitsPerSecondMsg, '256')}`,
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '5')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '7,8'),
        },
      ]}
      buttonText={buttonText}
    />
  );

  const LaitenettiL = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription('L')}
      {...sharedProps}
      analyticsIndex={analyticsIndexLaitenetti4G++}
      badges={[]}
      subHeader="Yritysliittymä"
      header="Laitenetti L"
      description={t.A7CE(
        'For surveillance and trail cameras (live video), for mobile routers, for network devices, for alarm devices'
      )}
      infoItems={[
        {
          icon: 'download',
          text: `${t.R4O4(downloadSpeed)} ${t.B69Q(kBitsPerSecondMsg, '512')}`,
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '10')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '11'),
        },
      ]}
      buttonText={buttonText}
    />
  );

  const Laitenetti5G = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription('5G')}
      {...sharedProps}
      analyticsIndex={analyticsIndexLaitenetti5G++}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      subHeader="Yritysliittymä"
      header="Laitenetti 5G"
      description={t.JQD9(
        'For uploading high quality live video. Allows you to benefit from new technological innovations and devices, as well as IOT applications and software.'
      )}
      infoItems={[
        {
          icon: 'download',
          text: `${t.R4O4(downloadSpeed)} ${t.IJNS(mBpsMsg, '50')}`,
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '100')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '28'),
        },
      ]}
      buttonText={buttonText}
    />
  );

  const shownCardList = subscriptionCards.map(subscriptionCard => subscriptionCard.cardName || '');

  const laitenetti4GCards: { name: string; component: React.FC }[] = [
    { name: 'S', component: LaitenettiS },
    { name: 'M', component: LaitenettiM },
    { name: 'L', component: LaitenettiL },
  ].filter(card => shownCardList.includes(card.name));

  const content = (
    <div className="of-subscriptions-main-container">
      {shownCardList.includes('5G') && (
        <>
          {hideGroupHeaders ? (
            <div className="of-subscriptions-main-container-header-spacer" />
          ) : (
            <h2 className="of-subscriptions-main-container-carousel-title">
              {t.OD42('5G Laitenetti package for companies')}
            </h2>
          )}
          <MobileCarouselWrapper>
            <Laitenetti5G />
          </MobileCarouselWrapper>
        </>
      )}
      {laitenetti4GCards.length > 0 && (
        <>
          {hideGroupHeaders ? (
            <div className="of-subscriptions-main-container-header-spacer" />
          ) : (
            <h2 className="of-subscriptions-main-container-carousel-title">
              {t.OD41('4G Laitenetti packages for companies')}
            </h2>
          )}
          <MobileCarouselWrapper>
            {laitenetti4GCards.map(card => {
              const Component = card.component;
              return <Component key={card.name} />;
            })}
          </MobileCarouselWrapper>
        </>
      )}
      <JsonLd<ItemList> item={getItemList(subscriptionCards as SubscriptionCardWithModel[], siteBaseUrl, pathname)} />
    </div>
  );

  if (compact) {
    return content;
  }

  return <Grid colWidthL={wide ? 12 : 10}>{content}</Grid>;
};
